import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, query, orderBy, limit, getDocs, where, Timestamp } from 'firebase/firestore';

const AUDIT_COLLECTION = 'audit_logs';

// ─── Action Type Registry ────────────────────────────────────────
export const ACTION_TYPES = {
    // Authentication
    AUTH_SESSION_START: { label: 'Sessie gestart', category: 'auth', severity: 'info' },
    AUTH_LOGOUT: { label: 'Uitgelogd', category: 'auth', severity: 'info' },
    AUTH_PROFILE_ERROR: { label: 'Profiel fout', category: 'auth', severity: 'warning' },
    LOGIN: { label: 'Ingelogd', category: 'auth', severity: 'info' },
    LOGOUT: { label: 'Uitgelogd', category: 'auth', severity: 'info' },

    // Permits
    PERMIT_CREATE: { label: 'Werkvergunning aangemaakt', category: 'permit', severity: 'info' },
    PERMIT_DELETE: { label: 'Werkvergunning verwijderd', category: 'permit', severity: 'critical' },
    PERMIT_DELETE_ERROR: { label: 'Verwijdering mislukt', category: 'permit', severity: 'error' },
    PERMIT_STATUS_CHANGE: { label: 'Status gewijzigd', category: 'permit', severity: 'info' },
    PERMIT_STATUS_ERROR: { label: 'Statuswijziging mislukt', category: 'permit', severity: 'error' },
    PERMIT_SAVE_ERROR: { label: 'Opslaan mislukt', category: 'permit', severity: 'error' },
    PERMIT_QUERY_ERROR: { label: 'Query fout', category: 'permit', severity: 'error' },
    PERMIT_ASSIGN_ERROR: { label: 'Toewijzing mislukt', category: 'permit', severity: 'error' },
    FORCE_UNLOCK_ERROR: { label: 'Force unlock mislukt', category: 'permit', severity: 'warning' },

    // Export
    EXPORT_PDF: { label: 'PDF geëxporteerd', category: 'export', severity: 'info' },
    EXPORT_CMMS: { label: 'CMMS export', category: 'export', severity: 'info' },

    // Data
    ROLE_CHANGE: { label: 'Rol gewijzigd', category: 'admin', severity: 'critical' },
    DATA_EXPORT: { label: 'Data geëxporteerd', category: 'export', severity: 'info' }
};

export const SEVERITY_LEVELS = {
    info: { label: 'Info', color: '#3b82f6' },
    warning: { label: 'Waarschuwing', color: '#f59e0b' },
    error: { label: 'Fout', color: '#ef4444' },
    critical: { label: 'Kritiek', color: '#dc2626' }
};

// ─── Integrity Hash ──────────────────────────────────────────────

/**
 * Compute a simple integrity hash for an audit entry
 * Uses Web Crypto API (SHA-256) for tamper detection
 * @param {object} entry - Audit log entry
 * @returns {Promise<string>} hex hash
 */
export async function computeIntegrityHash(entry) {
    // Let op: hier staat bewust hashedAt en niet timestamp.
    //
    // timestamp is serverTimestamp(), en dat is bij het schrijven een Firestore-
    // sentinel (voor elke regel identiek) en bij het lezen een Timestamp met
    // seconden en nanoseconden. Die twee kunnen nooit dezelfde hash opleveren,
    // waardoor verifyIntegrity() elke regel als geknoeid aanmerkte, ook een
    // logboek waar niemand aan had gezeten.
    //
    // hashedAt wordt door de client gezet als ISO-string en letterlijk
    // opgeslagen, dus die komt bij het lezen ongewijzigd terug. serverTimestamp
    // blijft bestaan voor de volgorde en als gezaghebbende tijd van de server.
    const canonical = JSON.stringify({
        userId: entry.userId,
        actionType: entry.actionType,
        details: entry.details,
        hashedAt: entry.hashedAt,
        locationId: entry.locationId
    });

    if (typeof crypto !== 'undefined' && crypto.subtle) {
        const encoder = new TextEncoder();
        const data = encoder.encode(canonical);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }

    // Fallback for environments without Web Crypto (e.g., test/jsdom)
    let hash = 0;
    for (let i = 0; i < canonical.length; i++) {
        const char = canonical.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16).padStart(8, '0');
}

export const auditService = {
    /**
     * Log a critical action for NIS2 compliance.
     * @param {string} userId - The UID of the actor (who did it).
     * @param {string} actionType - E.g. LOGIN, PERMIT_CREATE, PERMIT_DELETE, ROLE_CHANGE.
     * @param {object} details - Contextual data (e.g. { permitId: '123', oldStatus: 'draft' }).
     * @param {string} locationId - Optional location context.
     */
    async logAction(userId, actionType, details = {}, locationId = null) {
        try {
            const payload = {
                userId,
                actionType,
                details,
                locationId,
                // Gaat de hash in, en komt bij het lezen ongewijzigd terug.
                hashedAt: new Date().toISOString(),
                // Gezaghebbende tijd van de server, voor de volgorde. Blijft
                // buiten de hash, zie computeIntegrityHash.
                timestamp: serverTimestamp(),
                userAgent: navigator.userAgent,
                path: window.location.pathname,
                severity: ACTION_TYPES[actionType]?.severity || 'info',
                category: ACTION_TYPES[actionType]?.category || 'unknown'
            };

            // Compute integrity hash
            const hash = await computeIntegrityHash(payload);
            payload.integrityHash = hash;

            await addDoc(collection(db, AUDIT_COLLECTION), payload);
        } catch (error) {
            console.error("[ManagedAudit] FAILED to log action:", error);
        }
    },

    /**
     * Retrieve recent audit logs (Read-Only).
     *
     * scope: null = alles (superuser), [] = niets, anders die locaties.
     * De beveiligingsregels laten een beheerder alleen logregels van zijn
     * eigen locaties lezen, dus de query moet daar vooraf op filteren; een
     * bredere query wordt in zijn geheel geweigerd. Logregels zonder
     * locationId (platformfouten) zijn alleen voor de superuser.
     *
     * @param {number} maxLogs - Number of logs to fetch (default 50).
     * @param {null|string[]} scope - Uit permitScope(currentUser).
     */
    async getLogs(maxLogs = 50, scope = null) {
        try {
            const col = collection(db, AUDIT_COLLECTION);
            if (scope === null) {
                const q = query(col, orderBy('timestamp', 'desc'), limit(maxLogs));
                const querySnapshot = await getDocs(q);
                return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            }
            if (scope.length === 0) return [];
            const snaps = await Promise.all(scope.map(loc =>
                getDocs(query(col,
                    where('locationId', '==', loc),
                    orderBy('timestamp', 'desc'),
                    limit(maxLogs)))
            ));
            const alles = snaps.flatMap(s => s.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            const ms = t => (t && t.toMillis) ? t.toMillis() : new Date(t || 0).getTime();
            return alles.sort((a, b) => ms(b.timestamp) - ms(a.timestamp)).slice(0, maxLogs);
        } catch (error) {
            console.error("Error fetching audit logs:", error);
            return [];
        }
    },

    /**
     * Retrieve audit logs filtered by date range
     * @param {Date} fromDate - Start date
     * @param {Date} toDate - End date
     * @param {number} maxLogs - Max results
     * @returns {Promise<Array>}
     */
    async getLogsByDateRange(fromDate, toDate, maxLogs = 500) {
        try {
            const constraints = [
                orderBy('timestamp', 'desc'),
                limit(maxLogs)
            ];

            if (fromDate) {
                constraints.push(where('timestamp', '>=', Timestamp.fromDate(fromDate)));
            }
            if (toDate) {
                constraints.push(where('timestamp', '<=', Timestamp.fromDate(toDate)));
            }

            const q = query(collection(db, AUDIT_COLLECTION), ...constraints);
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("Error fetching audit logs by date:", error);
            return [];
        }
    },

    /**
     * Retrieve audit logs filtered by user
     * @param {string} userId
     * @param {number} maxLogs
     * @returns {Promise<Array>}
     */
    async getLogsByUser(userId, maxLogs = 200) {
        try {
            const q = query(
                collection(db, AUDIT_COLLECTION),
                where('userId', '==', userId),
                orderBy('timestamp', 'desc'),
                limit(maxLogs)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("Error fetching audit logs by user:", error);
            return [];
        }
    },

    /**
     * Retrieve audit logs filtered by action type
     * @param {string} actionType
     * @param {number} maxLogs
     * @returns {Promise<Array>}
     */
    async getLogsByAction(actionType, maxLogs = 200) {
        try {
            const q = query(
                collection(db, AUDIT_COLLECTION),
                where('actionType', '==', actionType),
                orderBy('timestamp', 'desc'),
                limit(maxLogs)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("Error fetching audit logs by action:", error);
            return [];
        }
    },

    /**
     * Compute statistics from audit logs
     * @param {Array} logs - Pre-fetched log entries
     * @returns {object} Stats: actionsByType, actionsByDay, actionsByUser, bySeverity
     */
    computeStats(logs) {
        const stats = {
            total: logs.length,
            byActionType: {},
            byCategory: {},
            bySeverity: {},
            byUser: {},
            byDay: {},
            criticalCount: 0,
            errorCount: 0
        };

        logs.forEach(log => {
            // By action type
            const action = log.actionType || 'UNKNOWN';
            stats.byActionType[action] = (stats.byActionType[action] || 0) + 1;

            // By category
            const category = log.category || ACTION_TYPES[action]?.category || 'unknown';
            stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;

            // By severity
            const severity = log.severity || ACTION_TYPES[action]?.severity || 'info';
            stats.bySeverity[severity] = (stats.bySeverity[severity] || 0) + 1;
            if (severity === 'critical') stats.criticalCount++;
            if (severity === 'error') stats.errorCount++;

            // By user
            const user = log.userId || 'unknown';
            stats.byUser[user] = (stats.byUser[user] || 0) + 1;

            // By day
            let day = 'unknown';
            if (log.timestamp?.toDate) {
                day = log.timestamp.toDate().toISOString().slice(0, 10);
            } else if (log.timestamp) {
                day = new Date(log.timestamp).toISOString().slice(0, 10);
            }
            stats.byDay[day] = (stats.byDay[day] || 0) + 1;
        });

        return stats;
    },

    /**
     * Export audit logs as CSV
     * @param {Array} logs
     * @returns {string} CSV content
     */
    exportToCSV(logs) {
        const headers = ['Datum/Tijd', 'Gebruiker', 'Actie', 'Categorie', 'Ernst', 'Details', 'Locatie', 'Browser', 'Hash'];

        const rows = logs.map(log => {
            let timestamp = '';
            if (log.timestamp?.toDate) {
                timestamp = log.timestamp.toDate().toLocaleString('nl-NL');
            } else if (log.timestamp) {
                timestamp = new Date(log.timestamp).toLocaleString('nl-NL');
            }

            const actionLabel = ACTION_TYPES[log.actionType]?.label || log.actionType;
            const details = JSON.stringify(log.details || {}).replace(/"/g, '""');

            return [
                timestamp,
                log.userId || '',
                actionLabel,
                log.category || '',
                log.severity || '',
                `"${details}"`,
                log.locationId || '',
                `"${(log.userAgent || '').substring(0, 50)}"`,
                log.integrityHash || ''
            ].join(',');
        });

        return [headers.join(','), ...rows].join('\n');
    },

    /**
     * Export audit logs as JSON
     * @param {Array} logs
     * @returns {object} Complete export envelope
     */
    exportToJSON(logs) {
        return {
            meta: {
                source: 'RaconWorks NIS2 Audit Trail',
                version: '1.0',
                exportedAt: new Date().toISOString(),
                recordCount: logs.length,
                standard: 'NIS2 Article 21 — Security Incident Logging'
            },
            actionTypes: ACTION_TYPES,
            logs: logs.map(log => ({
                ...log,
                timestamp: log.timestamp?.toDate
                    ? log.timestamp.toDate().toISOString()
                    : log.timestamp,
                actionLabel: ACTION_TYPES[log.actionType]?.label || log.actionType
            }))
        };
    },

    /**
     * Verify integrity of a set of audit logs
     * @param {Array} logs
     * @returns {Promise<{valid: number, invalid: number, unchecked: number}>}
     */
    async verifyIntegrity(logs) {
        let valid = 0, invalid = 0, unchecked = 0;

        for (const log of logs) {
            if (!log.integrityHash) {
                unchecked++;
                continue;
            }
            // Regels van voor de hashreparatie hebben geen hashedAt. Hun hash is
            // berekend over een serverTimestamp-sentinel en is dus per definitie
            // niet na te rekenen. Die tellen als niet controleerbaar en niet als
            // geknoeid: "wij kunnen dit niet verifieren" is waar, "hier is mee
            // geknoeid" zou een valse beschuldiging zijn.
            if (!log.hashedAt) {
                unchecked++;
                continue;
            }
            const computed = await computeIntegrityHash(log);
            if (computed === log.integrityHash) {
                valid++;
            } else {
                invalid++;
            }
        }

        return { valid, invalid, unchecked };
    }
};
